/**
 * Icon Demo Component
 * Demonstrates the Icon component sizes and accessibility features
 */
import { mdiAccount, mdiCog, mdiDownload, mdiHeart, mdiMagnify, mdiStar, } from "@mdi/js";
import React from "react";
import { nxmCollection, nxmDiscord, nxmInstall, nxmMod, nxmRosette, nxmVortex, } from "../../icon-paths";
import { Typography } from "../typography/Typography";
import { Icon } from "./Icon";
const mdiIcons = [
    { path: mdiAccount, label: "Account" },
    { path: mdiCog, label: "Settings" },
    { path: mdiDownload, label: "Download" },
    { path: mdiHeart, label: "Favorite" },
    { path: mdiMagnify, label: "Search" },
];
const nxmIcons = [
    { path: nxmVortex, label: "nxmVortex" },
    { path: nxmInstall, label: "nxmInstall" },
    { path: nxmMod, label: "nxmMod" },
    { path: nxmCollection, label: "nxmCollection" },
    { path: nxmRosette, label: "nxmRosette" },
    { path: nxmDiscord, label: "nxmDiscord" },
];
const colorIcons = [
    { path: mdiHeart, label: "Danger", className: "text-danger-strong" },
    { path: mdiStar, label: "Warning", className: "text-warning-strong" },
    { path: mdiDownload, label: "Success", className: "text-success-strong" },
    { path: mdiMagnify, label: "Info", className: "text-info-strong" },
];
export const IconDemo = () => (React.createElement("div", { className: "space-y-8" },
    React.createElement("div", { className: "rounded-sm bg-surface-mid p-4" },
        React.createElement(Typography, { as: "h2", typographyType: "heading-sm" }, "Icon"),
        React.createElement(Typography, { appearance: "subdued" },
            `SVG icon component with predefined sizes and accessibility support. Uses the `,
            React.createElement("span", { className: "font-semibold" }, "@mdi/js"),
            ` library for standard icons and custom `,
            React.createElement("span", { className: "font-semibold" }, "nxm*"),
            ` icon paths for Nexus Mods-specific icons.`)),
    React.createElement("div", { className: "space-y-4" },
        React.createElement(Typography, { as: "h3", typographyType: "heading-xs" }, "Sizes"),
        React.createElement("div", { className: "flex flex-wrap items-end gap-6" }, ["xs", "sm", "md", "lg", "xl", "2xl"].map((size) => (React.createElement("div", { className: "flex flex-col items-center gap-2", key: size },
            React.createElement(Icon, { path: mdiStar, size: size }),
            React.createElement(Typography, { appearance: "subdued", typographyType: "body-xs" }, size)))))),
    React.createElement("div", { className: "space-y-4" },
        React.createElement(Typography, { as: "h3", typographyType: "heading-xs" }, "With Title (Accessible)"),
        React.createElement(Typography, { appearance: "subdued", typographyType: "body-sm" },
            "When a title is provided, the icon gets role=\"img\" and a ",
            "<title>",
            "element for screen readers."),
        React.createElement("div", { className: "flex flex-wrap items-center gap-6" }, mdiIcons.map(({ path, label }) => (React.createElement("div", { className: "flex flex-col items-center gap-2", key: label },
            React.createElement(Icon, { path: path, size: "lg", title: label }),
            React.createElement(Typography, { appearance: "subdued", typographyType: "body-xs" }, label)))))),
    React.createElement("div", { className: "space-y-4" },
        React.createElement(Typography, { as: "h3", typographyType: "heading-xs" }, "Without Title (Decorative)"),
        React.createElement(Typography, { appearance: "subdued", typographyType: "body-sm" }, "Without a title, the icon gets role=\"presentation\" and is hidden from screen readers."),
        React.createElement("div", { className: "flex flex-wrap items-center gap-6" }, mdiIcons.map(({ path, label }) => (React.createElement(Icon, { key: label, path: path, size: "lg" }))))),
    React.createElement("div", { className: "space-y-4" },
        React.createElement(Typography, { as: "h3", typographyType: "heading-xs" }, "Custom NXM Icons"),
        React.createElement(Typography, { appearance: "subdued", typographyType: "body-sm" }, "Custom Nexus Mods icons from the icon-paths library, prefixed with nxm."),
        React.createElement("div", { className: "flex flex-wrap items-center gap-6" }, nxmIcons.map(({ path, label }) => (React.createElement("div", { className: "flex flex-col items-center gap-2", key: label },
            React.createElement(Icon, { path: path, size: "lg", title: label }),
            React.createElement(Typography, { appearance: "subdued", typographyType: "body-xs" }, label)))))),
    React.createElement("div", { className: "space-y-4" },
        React.createElement(Typography, { as: "h3", typographyType: "heading-xs" }, "Custom Colors"),
        React.createElement("div", { className: "flex flex-wrap items-center gap-6" }, colorIcons.map(({ path, label, className }) => (React.createElement(Icon, { className: className, key: label, path: path, size: "xl" })))))));
